import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitQuote } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { BUYER_TYPES, CATEGORIES } from "@/data/site";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-ink/20 bg-cream px-4 py-3 text-sm outline-none placeholder:text-sand/70 focus:border-kwila";

const INTERESTS = ["Furniture", "Solid timber doors", "Timber supply", "Decking & posts", "Bulk / project order", "Other"];
const CONTACT_PREFS = ["Email", "Phone"];

export default function RequestQuote() {
  const { items, clear } = useQuote();
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "", postcode: "",
    buyer_type: BUYER_TYPES[0], product_interest: INTERESTS[0], preferred_contact: CONTACT_PREFS[0], message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitQuote({
        ...form,
        items: items.map((i) => ({ product_id: i.id, name: i.name, qty: i.qty })),
      });
      setDone(true);
      clear();
      toast.success("Quote request sent", { description: "Our team will respond with pricing and lead times." });
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="request-quote-page">
      <section className="bg-ink px-5 pb-20 pt-32 text-cream md:px-10 md:pt-44">
        <div className="mx-auto max-w-[1500px]">
          <Kicker dark>Request a Quote</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-5xl font-light leading-[0.95] tracking-tight md:text-7xl">
            <MaskedLine delay={0.1}>One request.</MaskedLine>
            <MaskedLine delay={0.22} className="italic text-kwila">Every answer.</MaskedLine>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/70">
            Pricing, lead times, freight and availability — tell us what you need and our Archerfield team will
            come back with a complete quote. Plans and drawings can be emailed after submitting.
          </p>
        </div>
      </section>

      <section className="px-5 py-24 md:px-10">
        <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <Kicker>Your Quote List</Kicker>
              {items.length === 0 ? (
                <p className="mt-4 text-sm leading-relaxed text-ink/60" data-testid="rq-empty-list">
                  No products attached. You can submit a general enquiry below, or{" "}
                  <Link to="/shop" className="font-bold text-kwila underline underline-offset-2">browse the range</Link>{" "}
                  and add products first.
                </p>
              ) : (
                <ul className="mt-4 divide-y divide-ink/10 border-y border-ink/10" data-testid="rq-items-list">
                  {items.map((i) => (
                    <li key={i.id} className="flex items-center gap-4 py-3">
                      <img src={i.image} alt={i.name} className="h-12 w-12 rounded-sm object-cover" />
                      <div className="flex-1">
                        <p className="font-display text-lg leading-tight">{i.name}</p>
                        <p className="text-xs text-sand">Qty {i.qty}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-8 rounded-sm border border-kwila/25 bg-kwila/5 p-6">
                <p className="text-sm leading-relaxed text-ink/70">
                  <span className="font-bold text-kwila">Freight note.</span> Timber, doors and furniture are
                  bulky. Include your delivery postcode and access notes for an accurate freight quote — or
                  collect from Archerfield, Brisbane.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <Reveal className="flex h-full flex-col items-center justify-center rounded-sm border border-forest/30 bg-forest/5 p-12 text-center" data-testid="rq-success">
                <CheckCircle2 size={40} className="text-forest" />
                <p className="mt-4 font-display text-4xl">Quote request received.</p>
                <p className="mt-3 max-w-md text-sm text-ink/65">
                  Our team will respond with pricing, lead times and freight options. Need to add plans or
                  drawings? Reply to our confirmation email.
                </p>
                <Link to="/shop" data-testid="rq-success-shop-link" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila">
                  Continue Browsing <ArrowRight size={14} />
                </Link>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="rounded-sm border border-ink/15 bg-stone/40 p-6 md:p-10" data-testid="request-quote-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input data-testid="rq-name-input" required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} />
                    <input data-testid="rq-business-input" placeholder="Business name" value={form.business_name} onChange={set("business_name")} className={inputCls} />
                    <input data-testid="rq-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                    <input data-testid="rq-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                    <input data-testid="rq-postcode-input" placeholder="Location / postcode" value={form.postcode} onChange={set("postcode")} className={inputCls} />
                    <select data-testid="rq-buyer-type-select" value={form.buyer_type} onChange={set("buyer_type")} className={inputCls}>
                      {BUYER_TYPES.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <select data-testid="rq-interest-select" value={form.product_interest} onChange={set("product_interest")} className={inputCls}>
                      {INTERESTS.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                    <select data-testid="rq-contact-pref-select" value={form.preferred_contact} onChange={set("preferred_contact")} className={inputCls}>
                      {CONTACT_PREFS.map((c) => (
                        <option key={c} value={c}>Preferred contact: {c}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    data-testid="rq-message-input"
                    rows={5}
                    placeholder="Species, sizes, quantities, door dimensions, delivery notes, timing…"
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputCls} mt-4`}
                  />
                  <p className="mt-4 text-xs text-ink/50">
                    Plans, drawings and photos can be emailed after submitting this form. [File upload to be
                    enabled by client.]
                  </p>
                  <button
                    data-testid="rq-submit-button"
                    disabled={busy}
                    className="mt-6 flex items-center gap-2 rounded-full bg-kwila px-10 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-ink disabled:opacity-50"
                  >
                    {busy ? "Sending…" : "Submit Quote Request"} <ArrowRight size={14} />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
